import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { UrlAnswerList } from "@/components/url-answer-list";
import { client } from "@/hera-api/client/client.gen";
import type {
  LicenseResponseSchema,
  RegistrantQuestionsResponseSchema,
} from "@/hera-api/client/types.gen";
import { documentAnswerTokens } from "@/lib/answer-file";
import { isNacStyleAnswerType } from "@/lib/nac-answer-types";
import { normalizePhotoAnswers } from "@/lib/photo-answer";
import { trackedFetch } from "@/lib/server-request-activity";
import {
  formatAnswerValue,
  formatConfirmationAnswer,
  formatPhoneNumber,
} from "@/lib/utils";
import { LiveLinkAnswer } from "./live-link-answer";
import { PhotoAnswerReview } from "./photo-answer-review";

interface AdditionalQuestionsCardContentProps {
  additionalQuestions?: RegistrantQuestionsResponseSchema;
  data: LicenseResponseSchema;
}

export function AdditionalQuestionsCardContent({
  additionalQuestions,
  data,
}: AdditionalQuestionsCardContentProps) {
  const handleShowDocument = async (token: string) => {
    const baseUrl = client.getConfig().baseUrl || "";
    const auth = client.getConfig().auth;
    const url = `${baseUrl}/shared/answer_files/${token}`;

    try {
      // Fetch the file with authentication
      const response = await trackedFetch(url, {
        headers: {
          Authorization: `Bearer ${auth}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load document: ${response.statusText}`);
      }

      // Get the file as a blob
      const blob = await response.blob();

      // Create a blob URL and open it in a new tab
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");

      // Clean up the blob URL after a delay (the browser will keep it alive while the tab is open)
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error("Error loading document:", error);
      // Fallback: try opening the URL directly (might prompt for auth)
      window.open(url, "_blank");
    }
  };

  // Only show registration question types; inspection/deregistration are shown elsewhere or not on registration details
  const registrationQuestions = additionalQuestions?.questions.filter(
    (q) => q.question_type === "registration",
  );

  const answers = data.answers_to_additional_questions ?? {};

  const hasAnswer = (answer: unknown): boolean => {
    if (answer === undefined || answer === null) return false;
    if (typeof answer === "string" && answer.trim() === "") return false;
    if (Array.isArray(answer) && answer.length === 0) return false;
    return true;
  };

  return (
    <CardContent className="space-y-4">
      {registrationQuestions
        ?.filter(
          (question) =>
            question.answer_type !== "information" &&
            !isNacStyleAnswerType(question.answer_type) &&
            hasAnswer(answers[question.id]),
        )
        .map((question) => {
          const answer = answers[question.id];

          // Handle document type specially
          if (question.answer_type === "document") {
            const tokens = documentAnswerTokens(answer);
            if (tokens.length === 0) return null;
            return (
              <div key={question.id}>
                <p className="text-sm font-bold">{question.question}</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {tokens.map((token, index) => (
                    <Button
                      key={token}
                      variant="outline"
                      size="sm"
                      onClick={() => handleShowDocument(token)}
                    >
                      {tokens.length > 1 ? `Show file ${index + 1}` : "Show"}
                    </Button>
                  ))}
                </div>
              </div>
            );
          }

          if (question.answer_type === "url") {
            return (
              <div key={question.id}>
                <p className="text-sm font-bold">{question.question}</p>
                <UrlAnswerList value={answer} />
              </div>
            );
          }

          if (question.answer_type === "photo") {
            const photos = normalizePhotoAnswers(answer);
            if (photos.length === 0) return null;
            return (
              <div key={question.id}>
                <p className="text-sm font-bold">{question.question}</p>
                <PhotoAnswerReview photos={photos} property={data.property} />
              </div>
            );
          }

          // Live Link: render a clickable link to the configured URL template.
          if (
            question.answer_type === "live_link" &&
            typeof answer === "string" &&
            answer
          ) {
            return (
              <div key={question.id}>
                <p className="text-sm font-bold">{question.question}</p>
                <LiveLinkAnswer
                  value={answer}
                  urlTemplate={question.url_template}
                  copyValueToClipboard={
                    question.copy_value_to_clipboard ?? false
                  }
                  httpMethod={question.http_method}
                  valueExtractRegex={question.value_extract_regex}
                  requestFields={question.request_fields}
                  browserHelperConfig={question.browser_helper_config}
                />
              </div>
            );
          }

          if (question.answer_type === "confirmation") {
            const statement = question.information_text?.trim();
            return (
              <div key={question.id}>
                <p className="text-sm font-bold">{question.question}</p>
                {statement && statement !== question.question ? (
                  <p className="text-sm text-muted-foreground">{statement}</p>
                ) : null}
                <p className="text-sm">{formatConfirmationAnswer(answer)}</p>
              </div>
            );
          }

          const formattedAnswer =
            question.answer_type === "phone_number" &&
            typeof answer === "string"
              ? formatPhoneNumber(answer)
              : formatAnswerValue(answer);

          return (
            <div key={question.id}>
              <p className="text-sm font-bold">{question.question}</p>
              <p className="text-sm">{formattedAnswer}</p>
            </div>
          );
        })}
    </CardContent>
  );
}
